import { React, useState, useEffect, classNames } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import scrollIntoView from 'scroll-into-view';
import throttle from 'lodash.throttle';
import { List } from '@digsup/components-list';
import { Button } from '@digsup/components-button';
import { Icon } from '@digsup/components-icon';

import { styles } from './Nav.scss';

const cx = classNames.bind(styles);

const NAV_SCROLLED_OFFSET = 140;
const NAV_SCROLLED_OFFSET_DEFAULT = 30;

export const Nav = ({
  variant,
  logoHref,
  partnerHref,
  pricingHref,
  featuresHref,
  onLogIn,
  isAuthenticated,
  dashboardHref,
  ...props
}) => {
  const [isScrolled, setScrolled] = useState(false);
  const [mobileMenuActive, setMobileMenuActive] = useState(false);

  const mobileScrollTo = targetId => {
    setMobileMenuActive(false);

    setTimeout(() => {
      scrollIntoView(document.getElementById(targetId));
    }, 350);
  };

  function onScroll() {
    const offset = window.scrollY;

    const scrollOffset =
      variant === 'interactive'
        ? NAV_SCROLLED_OFFSET
        : NAV_SCROLLED_OFFSET_DEFAULT;

    if (offset > scrollOffset) {
      if (!isScrolled) {
        setScrolled(true);
      }
    } else if (isScrolled) {
      setScrolled(false);
    }
  }

  useEffect(() => {
    const throttledOnScroll = throttle(onScroll);
    function watchScroll() {
      window.addEventListener('scroll', throttledOnScroll);
    }
    watchScroll();
    return () => {
      window.removeEventListener('scroll', throttledOnScroll);
    };
  });

  const navClasses = cx('l-homepage-nav', {
    'l-homepage-nav--active': mobileMenuActive,
    'l-homepage-nav--scrolled': isScrolled,
    [`l-homepage-nav--${variant}`]: variant,
  });

  const wrapperClasses = cx('l-homepage-nav__wrapper', {
    [`l-homepage-nav__wrapper--${variant}`]: variant,
  });

  const mobileListClasses = cx(
    'u-visuallyhidden@medium u-visuallyhidden@large u-visuallyhidden@xlarge l-homepage-nav__mobile-list',
    {
      'l-homepage-nav__mobile-list--active': mobileMenuActive,
    },
  );

  const toggleIconClasses = cx('l-homepage-nav__toggle-icon', {
    'l-homepage-nav__toggle-icon--active': mobileMenuActive,
  });

  return (
    <>
      {variant === 'default' && (
        <div className="u-margin-bottom-large u-margin-bottom-medium@medium l-homepage-nav-placeholder" />
      )}
      <nav className={mobileListClasses}>
        <div className="l-homepage-nav__mobile-list-inner">
          <div className="u-margin-bottom-large">
            <Button
              variant="flat"
              onClick={featuresHref ? null : () => mobileScrollTo('features')}
              href={featuresHref || null}
              className="l-homepage-nav__mobile-list-btn">
              Features
            </Button>
          </div>
          <div className="u-margin-bottom-large">
            <Button
              variant="flat"
              onClick={pricingHref ? null : () => mobileScrollTo('pricing')}
              href={pricingHref || null}
              className="l-homepage-nav__mobile-list-btn">
              Pricing
            </Button>
          </div>
          <div>
            <Button
              variant="primary"
              size="large"
              onClick={onLogIn}
              className="l-homepage-nav__mobile-list-sign-btn">
              Sign Up / Login
            </Button>
          </div>
        </div>
      </nav>

      <div className={navClasses} {...props}>
        <div className="l-homepage-nav__container">
          <div className={wrapperClasses}>
            <List display="flex" align="space-between" valign="center">
              <List.Item>
                <Button
                  variant="flat"
                  iconOnly
                  icon={[
                    <Icon
                      key="1"
                      name="logo-monochrome"
                      className="l-homepage-nav__logo"
                    />,
                    <Icon
                      key="2"
                      name="logo"
                      className="l-homepage-nav__active-logo"
                    />,
                  ]}
                  onClick={
                    logoHref
                      ? null
                      : () => {
                          if (mobileMenuActive) {
                            mobileScrollTo('hero');
                          } else {
                            scrollIntoView(document.getElementById('hero'));
                          }
                        }
                  }
                  href={logoHref || null}
                />
              </List.Item>
              <List.Item>
                <nav className="u-visuallyhidden@xsmall u-visuallyhidden@small">
                  <List spacing="xlarge" display="flex" valign="center" nowrap>
                    <List.Item>
                      <Button
                        variant="flat"
                        onClick={
                          featuresHref
                            ? null
                            : () =>
                                scrollIntoView(
                                  document.getElementById('features'),
                                )
                        }
                        href={featuresHref || null}>
                        Features
                      </Button>
                    </List.Item>
                    <List.Item>
                      <Button
                        variant="flat"
                        onClick={
                          pricingHref
                            ? null
                            : () =>
                                scrollIntoView(
                                  document.getElementById('pricing'),
                                )
                        }
                        href={pricingHref || null}>
                        Pricing
                      </Button>
                    </List.Item>
                    {isAuthenticated ? (
                      <List.Item>
                        <Button variant="flat" href={dashboardHref}>
                          Dashboard
                        </Button>
                      </List.Item>
                    ) : (
                      <>
                        <List.Item>
                          <Button
                            variant="flat"
                            onClick={
                              partnerHref
                                ? null
                                : () =>
                                    scrollIntoView(
                                      document.getElementById('partner'),
                                    )
                            }
                            href={partnerHref || null}>
                            Partner
                          </Button>
                        </List.Item>
                        <List.Item>
                          <Button
                            variant="primary"
                            onClick={onLogIn}
                            className="l-homepage-nav__signup-btn">
                            Sign Up / Login
                          </Button>
                        </List.Item>
                      </>
                    )}
                  </List>
                </nav>
                <Button
                  variant="flat"
                  onClick={() => setMobileMenuActive(!mobileMenuActive)}
                  className="u-visuallyhidden@medium u-visuallyhidden@large u-visuallyhidden@xlarge">
                  <div className={toggleIconClasses}>
                    <span />
                  </div>
                </Button>
              </List.Item>
            </List>
          </div>
        </div>
      </div>
    </>
  );
};

Nav.propTypes = {
  /**
   * Nav variant: interactive(toggle background on scroll), default(static on scroll)
   */
  variant: PropTypes.oneOf(['interactive', 'default']),
  /**
   * Logo btn href - used for nav outside Homepage
   */
  logoHref: PropTypes.any,
  /**
   * featuresHref - used for nav outside Homepage
   */
  featuresHref: PropTypes.any,
  /**
   * pricingHref - used for nav outside Homepage
   */
  pricingHref: PropTypes.any,
  /**
   * partnerHref - used for nav outside Homepage
   */
  partnerHref: PropTypes.any,
  /**
   * LogIn btn callback
   */
  onLogIn: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  dashboardHref: PropTypes.any,
};

Nav.defaultProps = {
  variant: 'interactive',
};
